import React, { Component } from "react";
import { AppCard } from "../../components/ui";

import { merchantDocumentApiService } from "../../services/api/merchants/MerchantDocumentApiService";
import { toastService } from "../../services/toastService";
import { DocumentTypeModel } from "../../models/DocumentTypeModel";
import { utils } from "../../utils/utils";
import { AddKycDocument, DocumentFile } from "../../models/KycSubmission";
import { kycSubmissionApiService } from "../../services/api/Kyc/KycSubmissionApiService";
import classNames from "classnames";
import { localeOption } from "primereact/api";
import { ProgressBar } from "primereact/progressbar";
import { Ripple } from "primereact/ripple";
import { Plus, X, FileText, FileImage, File } from "lucide-react";
import ExtendedDropdown from "../../components/ExtendedDropdown";
import { DataTable } from "primereact/datatable";
import { Column, ColumnBodyOptions } from "primereact/column";
import { fileService } from "../../services/FileService";
import { userApiService } from "../../services/api/users/UsersApiService";

// Matches CRM's KycSubmissionConstants.DocumentFileNameLengthLimit: Flexcube cuts the checklist DocumentType at
// 20 characters, so a KYC document name must be shorter than this (TNPDEVAPPS-1997).
const KYC_DOCUMENT_FILE_NAME_LENGTH_LIMIT = 20;

interface AddManualKycProps {
    //    onDialogClose: (success: boolean) => void;
    submissionId: number;
    submitToOwner: (documents: DocumentFile[]) => void;
    //    renderIn:string;
}

interface AddManualKycState {
    isLoading: boolean;
    documents: DocumentFile[];
    documentTypes: DocumentTypeModel[];
    allAllowedExtensions: Set<string>;
    focused: boolean;
    progress: number;
    validationErrors: Map<string, boolean>;
}

export default class AddManualKyc extends Component<AddManualKycProps, AddManualKycState> {
    maxFileSize: number = 5242880;

    invalidFileSizeMessageSummary: string = "{0}: Invalid file size, ";
    invalidFileSizeMessageDetail: string = "maximum upload size is {0}.";

    fileInput: HTMLInputElement | null = null;
    fileUploadContent: HTMLDivElement | null = null;

    constructor(props: AddManualKycProps) {
        super(props);

        this.state = {
            isLoading: false,
            documents: [],
            documentTypes: [],
            allAllowedExtensions: new Set<string>(),
            focused: false,
            progress: 0,
            validationErrors: new Map<string, boolean>(),
        };
    }

    async componentDidMount() {
        this.setState({ isLoading: true });

        const documentsResult = await kycSubmissionApiService.getDocuments(this.props.submissionId);
        if (!documentsResult.success) {
            toastService.error(documentsResult.message);
            return;
        }

        const localDocuments = this.state.documents;
        documentsResult.data.forEach(async (kycDocument) => {
            localDocuments.push({
                file: null,
                documentType: kycDocument.documentType,
                isUploaded: false,
                objectURL: null,
                fileName: kycDocument.fileName,
                fileStorageId: kycDocument.fileStorageId,
            });
        });
        this.setState({ documents: localDocuments });
        this.onDocumentUpdate();

        const documentTypes = await userApiService.getIndividualDocumentsTypes();
        if (!documentTypes.success) {
            toastService.error(documentTypes.message);
        }

        // Compute allowed extensions once when document types are loaded
        const allAllowedExtensions = new Set<string>();
        documentTypes.data.forEach((dt) => {
            dt.allowedExtensions.forEach((ext) => allAllowedExtensions.add(ext.toLowerCase()));
        });

        this.setState({ documentTypes: documentTypes.data, allAllowedExtensions });

        this.setState({ isLoading: false });
    }

    onDocumentUpdate = async () => {
        this.props.submitToOwner(this.state.documents);
    };

    onDownloadClick = async (fileStorageId: string, secondaryLabel: string): Promise<string> => {
        this.setState({ isLoading: true });
        const imagePath = await fileService.GetFile(fileStorageId);

        if (!imagePath.success) {
            toastService.error(imagePath.message);
            return "";
        }

        const url = utils.appendBase64Data(imagePath.data, fileStorageId);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", secondaryLabel);
        document.body.appendChild(link);
        link.click();
        this.setState({ isLoading: false });
        return "ok";
    };

    hasFiles() {
        return this.state.documents && this.state.documents.length > 0;
    }

    isImage(file: File) {
        return /^image\//.test(file.type);
    }

    onRemoveFile(index: number) {
        //There is no remove command at the server, so ... use it only for not uploaded documents
        this.clearInputElement();
        const currentDocuments = [...this.state.documents];

        currentDocuments.splice(index, 1);
        this.setState({ documents: currentDocuments });
        this.onDocumentUpdate();
    }

    clearInputElement() {
        if (this.fileInput) {
            this.fileInput.value = "";
        }
    }

    formatSize(bytes: number) {
        if (bytes === 0) {
            return "0 B";
        }
        const k = 1024,
            dm = 3,
            sizes = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"],
            i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    }

    onFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files![0];
        if (this.isFileAlreadySelected(file)) {
            this.showFileError("This File is already Selected.");
            return;
        }

        if (!this.validateFile(file)) {
            return;
        }

        // Same rules CRM enforces on PUT /KycSubmission (TNPDEVAPPS-1997), checked here so the officer hears
        // about every bad name before Save uploads the files one by one and stops at the first refusal.
        if (file.name.length >= KYC_DOCUMENT_FILE_NAME_LENGTH_LIMIT) {
            this.showFileError(`File name must be less than ${KYC_DOCUMENT_FILE_NAME_LENGTH_LIMIT} characters. Rename the file and choose it again.`);
            return;
        }

        if (this.isFileNameTaken(file.name)) {
            this.showFileError(`A document with file name ${file.name} already exists for this KYC submission.`);
            return;
        }

        const objectURL = this.isImage(file) ? window.URL.createObjectURL(file) : "";

        const documentFile: DocumentFile = {
            file: file,
            documentType: "",
            isUploaded: false,
            objectURL: objectURL,
            fileName: file.name,
            fileStorageId: "",
        };

        this.setState({ documents: [...this.state.documents, documentFile] });
        this.onDocumentUpdate();
    };

    // A file that cannot be added gets a red toast that stays until closed, and the input is cleared so the same
    // file can be chosen again after renaming.
    showFileError(detail: string) {
        toastService.error(detail, "Error", true);
        this.clearInputElement();
    }

    // Case-insensitive, like CRM. Covers the documents already on the submission and the ones chosen but not yet saved.
    isFileNameTaken(fileName: string) {
        const lowerFileName = fileName.toLowerCase();
        return this.state.documents.some((document) => (document.file?.name ?? document.fileName).toLowerCase() === lowerFileName);
    }

    isFileAlreadySelected(files: File) {
        for (const sFile of this.state.documents) {
            if (sFile.file !== null && sFile.file.name + sFile.file.type + sFile.file.size === files.name + files.type + files.size) return true;
        }
        return false;
    }

    validateFile(file: File) {
        // Client-side file size check as UX hint (backend will also validate)
        // Accept files up to and including 5MB (5242880 bytes)
        if (file.size > this.maxFileSize) {
            this.showFileError(
                this.invalidFileSizeMessageSummary.replace("{0}", file.name) +
                    this.invalidFileSizeMessageDetail.replace("{0}", this.formatSize(this.maxFileSize)),
            );
            return false;
        }

        // Client-side extension check as UX hint (backend will also validate per document type)
        // Split by dot and get the last part as extension
        const parts = file.name.split(".");
        const fileExtension = parts.length > 1 ? parts[parts.length - 1]?.trim().toLowerCase() : "";

        // Validate that extension exists and is not empty (handles cases like "file." or "file")
        if (!fileExtension || fileExtension.length === 0) {
            this.showFileError("File must have an extension.");
            return false;
        }

        // Ensure document types configuration is loaded
        if (!this.state.allAllowedExtensions || this.state.allAllowedExtensions.size === 0) {
            this.showFileError("Document types are still loading. Please wait a moment and try again.");
            return false;
        }

        // Check if any document type allows this extension (using cached Set from componentDidMount)
        if (!this.state.allAllowedExtensions.has(fileExtension)) {
            const allowedList = Array.from(this.state.allAllowedExtensions).sort().join(", ");
            this.showFileError(`File extension '.${fileExtension}' is not allowed. Allowed extensions: ${allowedList}`);
            return false;
        }

        return true;
    }

    choose = () => {
        this.fileInput?.click();
    };

    onKeyDown = (event: any) => {
        // enter
        if (event.which === 13) {
            this.choose();
        }
    };

    onFieldUpdate = (value: any, index: number) => {
        this.setState((prevState) => {
            return (prevState.documents[index].documentType = value);
        });
        this.onDocumentUpdate();
    };

    renderChooseButton() {
        const chooseClassName = classNames("p-button p-fileupload-choose p-component", {
            "p-disabled": false,
            "p-focus": this.state.focused,
            "p-button-icon-only": false,
        });

        return (
            <span
                className={chooseClassName}
                onClick={this.choose}
                onKeyDown={this.onKeyDown}
                onFocus={() => this.setState({ focused: true })}
                onBlur={() => this.setState({ focused: false })}
                tabIndex={0}
            >
                <input
                    ref={(el) => (this.fileInput = el)}
                    type="file"
                    onChange={this.onFileSelect}
                    multiple={false}
                    accept="image/*,application/pdf"
                />
                <Plus size={16} />
                <span>{localeOption("choose", "")}</span>
                <Ripple />
            </span>
        );
    }

    renderProgressBarContent() {
        return <ProgressBar value={this.state.progress} showValue={false} />;
    }

    renderImage(files: DocumentFile) {
        if (files.objectURL) {
            return (
                <div>
                    <img alt={files.file?.name ?? files.fileName} role="presentation" src={files.objectURL} width={50} />
                </div>
            );
        }

        const ext = files.fileName ? files.fileName.substring(files.fileName.length - 3).toLowerCase() : "";
        let IconComponent = File;
        let iconColor = "text-neutral-400";
        if (ext === "pdf") {
            IconComponent = FileText;
            iconColor = "text-danger-500";
        } else if (["png", "jpg", "bmp"].includes(ext)) {
            IconComponent = FileImage;
            iconColor = "text-primary-500";
        }

        return (
            <div className="w-[50px] h-[50px] flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 rounded-lg">
                <IconComponent size={24} strokeWidth={1.5} className={iconColor} />
            </div>
        );
    }

    setValidationErrors = (name: string, value: boolean) => {
        const validationErrors = this.state.validationErrors;

        if (validationErrors.get(name) === value) {
            return;
        }

        validationErrors.set(name, value);
        this.setState({ validationErrors: validationErrors });
    };

    DocumentTypes = (documentType: string, index: number, fileName: string) => {
        return (
            <div>
                <ExtendedDropdown
                    name="Document/File Type"
                    style={{ display: "flex" }}
                    value={documentType}
                    options={this.state.documentTypes}
                    optionLabel="displayName"
                    optionValue="code"
                    filter
                    filterBy="displayName"
                    onChange={(e) => this.onFieldUpdate(e.value, index)}
                    required={true}
                    placeholder={"Select a Type"}
                    onValidStatusChanged={(value: boolean) => this.setValidationErrors(`image_${fileName}`, value)}
                />
            </div>
        );
    };

    fileName = (name: string) => {
        return <div className="p-fileupload-filename">{name}</div>;
    };

    fileSize = (size: number) => {
        return <div>{this.formatSize(size)}</div>;
    };

    deleteBody = (index: number, storageId: string) => {
        if (storageId == "")
            return (
                <>
                    <button type="button" className="bg-transparent border-0 cursor-pointer p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors" tabIndex={0} onClick={() => this.onRemoveFile(index)}>
                        <X size={16} className="cursor-pointer text-neutral-500 hover:text-danger-500 transition-colors" />
                    </button>
                </>
            );
    };

    DownloadBody = (storageId: string) => {
        if (storageId != "")
            return (
                <>
                    <a href="#" onClick={() => this.onDownloadClick(storageId, "")}>
                        Download
                    </a>
                </>
            );
    };

    renderFileUploader() {
        let progressBar;

        if (this.hasFiles()) {
            progressBar = this.renderProgressBarContent();
        }

        return (
            <div className="p-fileupload p-fileupload-advanced p-component">
                <div
                    ref={(el) => {
                        this.fileUploadContent = el;
                    }}
                    className="p-fileupload-content"
                >
                    {progressBar}
                    <div className="p-fileupload-files">
                        <DataTable header={this.renderChooseButton()} value={this.state.documents} loading={this.state.isLoading}>
                            <Column header="" body={(rowData: DocumentFile) => this.renderImage(rowData)} />
                            <Column header="File Name" body={(rowData: DocumentFile) => this.fileName(rowData.file?.name ?? rowData.fileName)} />
                            {/* <Column header="File Size" body={(rowData: DocumentFile) => this.fileSize(rowData.file?.size ?? 0)} /> */}
                            <Column
                                header="File or Document Type"
                                body={(rowData: DocumentFile, props: ColumnBodyOptions) =>
                                    this.DocumentTypes(rowData.documentType, props.rowIndex, rowData.file?.name ?? "")
                                }
                            />
                            <Column
                                header=""
                                body={(rowData: DocumentFile, props) => this.DownloadBody(rowData.fileStorageId)}
                                headerStyle={{ width: "8em", textAlign: "center" }}
                                bodyStyle={{ textAlign: "center", overflow: "visible" }}
                            />
                            <Column
                                header=""
                                body={(rowData: DocumentFile, props) => this.deleteBody(props.rowIndex, rowData.fileStorageId)}
                                headerStyle={{ width: "8em", textAlign: "center" }}
                                bodyStyle={{ textAlign: "center", overflow: "visible" }}
                            />
                        </DataTable>
                    </div>
                </div>
            </div>
        );
    }

    renderModalBody = () => {
        return (
            <AppCard className="mb-3">
                <AppCard.Body>
                    <form>{this.renderFileUploader()}</form>
                </AppCard.Body>
            </AppCard>
        );
    };

    render() {
        //        if (this.props.renderIn == "kycMatrix")
        //        {
        return <div>{this.renderFileUploader()}</div>;
        //        }
        // else
        // {
        //     const failedErrors = Array.from(this.state.validationErrors.values())
        //         .filter(f => f == false);

        //     return (
        //         <div>
        //             <Toast ref={(el) => (this.toast = el)} position={"bottom-center"} baseZIndex={99999}></Toast>
        //             <Dialog
        //                 header="Add Manual KYC Submission"
        //                 visible={true}
        //                 style={{ width: "70vw" }}
        //                 onHide={() => this.props.onDialogClose(false)}
        //             >
        //                 {this.renderModalBody()}
        //                 <div className="flex justify-end gap-2 pt-4 border-t border-neutral-200 dark:border-neutral-700">
        //                     <Button
        //                         label="Submit"
        //                         disabled={failedErrors.length > 0 || this.state.isLoading || !this.hasFiles()}
        //                         onClick={() => this.onSubmitKycSubmissionDocument()}
        //                     />
        //                 </div>
        //             </Dialog>
        //         </div>
        //     );
        // }
    }
}
